import { forwardRef } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * Button. See docs/DESIGN_SYSTEM.md §6.2.
 *
 * `primary` pairs --color-action with --color-on-action rather than hard-coding white.
 * Light mode is white on brand-700 (5.48:1); dark mode inverts to near-black on a vivid
 * emerald (7.88:1), because white on any green light enough to read against a dark
 * surface measures below 4.5:1 and fails AA.
 *
 * The filled variants carry `--shadow-action`: a brand-tinted drop shadow plus an inner
 * top highlight. That pair is what makes a filled control read as a physical object
 * rather than a coloured rectangle, and it costs nothing at runtime.
 */
const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-1.5 whitespace-nowrap',
    'rounded-md font-medium',
    'transition-[background-color,box-shadow,transform,color] duration-150 ease-out',
    // A 1% press. Enough to feel mechanical, small enough never to look bouncy.
    'active:scale-[0.99]',
    'disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none',
    // Touch targets reach 44px via a transparent expansion on coarse pointers. It
    // expands on BOTH axes: the vertical-only version left every icon-only button at
    // 36px wide, which is the size most often missed on a phone.
    'relative after:absolute after:left-1/2 after:top-1/2 after:h-11 after:min-w-11 after:w-full',
    'after:-translate-x-1/2 after:-translate-y-1/2 after:content-[""]',
    '[@media(pointer:fine)]:after:hidden',
  ],
  {
    variants: {
      variant: {
        primary:
          'bg-action text-on-action shadow-action hover:bg-action-hover active:bg-action-active',
        secondary:
          'border border-line-strong bg-surface text-fg shadow-xs hover:bg-hover hover:border-line-strong active:bg-subtle',
        ghost: 'text-fg-muted hover:bg-hover hover:text-fg active:bg-subtle',
        danger:
          'bg-danger-fill text-white shadow-xs hover:brightness-[0.93] active:brightness-[0.88]',
        link: 'text-link underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-8 px-2.5 text-label',
        md: 'h-9 px-3.5 text-body',
        lg: 'h-11 px-5 text-body-lg',
        icon: 'size-9 p-0',
      },
    },
    defaultVariants: { variant: 'secondary', size: 'md' },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, asChild = false, loading = false, disabled, children, ...props },
  ref,
) {
  const classes = cn(buttonVariants({ variant, size }), className);

  // `asChild` renders the caller's element (a Link, say) with button styling. Radix's
  // Slot requires EXACTLY ONE element child, so the loading spinner must not be added
  // here — a `{loading && …}` expression yields a second child and Slot throws. A
  // spinner is meaningless in this mode anyway: the child owns its content.
  if (asChild) {
    return (
      <Slot ref={ref} className={classes} {...props}>
        {children}
      </Slot>
    );
  }

  return (
    <button
      ref={ref}
      className={classes}
      disabled={disabled ?? loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {/* The label persists while loading, so the button does not change width and
          shift the layout around it. */}
      {loading && <Loader2 aria-hidden className="size-3.5 animate-spin" />}
      {children}
    </button>
  );
});

export { buttonVariants };
